import { Particle } from "../particle";
import { colorLerp, constant, easeOut, lerp, linearMotion, randomBetween } from "../interpolation";

/**
 * Gray smoke wisps that drift upward from a damaged machine.
 * Used as a continuous damage indicator for robot mobs
 * (MiningRobot, PlowRobot, ArtilleryRobot). Visually distinct
 * from MissileExhaustParticle — this is slower, grayer, and
 * grows as it rises (like smoke dissipating).
 */
export class DamageSmokeParticle extends Particle {
	/** Emission interval for generators (ms between particles). */
	static readonly emitIntervalMs = 150;

	constructor(originX: number, originY: number) {
		const ttl = randomBetween(800, 1200);

		// Slight random horizontal drift — smoke doesn't rise perfectly straight
		const vx = randomBetween(-0.3, 0.3);

		// Slow upward drift (negative Y = up on screen)
		const vy = randomBetween(-3, -0.3);

		const startSize = randomBetween(0.35, 0.45);
		const endSize = randomBetween(0.7, 1.2);

		// Near-black at birth, easing to mid-gray — kept well below concrete/rubble
		// grays so the smoke stays visible on DestroyedConcreteBlock tiles.
		const darkGray = {
			r: randomBetween(10, 25),
			g: randomBetween(10, 25),
			b: randomBetween(15, 30),
		};
		const lightGray = {
			r: randomBetween(65, 90),
			g: randomBetween(65, 90),
			b: randomBetween(70, 95),
		};

		// Randomize spawn position
		const ox = originX + randomBetween(-0.3, 0.3);
		const oy = originY + randomBetween(-0.5, -0.35);

		super(ox, oy, {
			ttl,
			size: lerp(startSize, endSize),
			opacity: easeOut(0.9, 0.4),
			color: colorLerp(darkGray, lightGray),
			rotation: constant(0),
			position: linearMotion(vx, vy),
		});
	}
}
